import { UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'
import {
  getB2BOrders,
  getB2BOutboundDocumentDetail,
  getExpectedAllocaton,
  getOrders,
  runStandardOrderCutoff,
} from '@/services/outbound/outboundWaitingStatus/apis'
import {
  GetExpectedAllocationReq,
  GetOrdersReq,
  GetOrdersDownload,
  B2BGetOrdersReq,
  GetB2BOutboundDocumentDetailReq,
  OrderItem,
  RunStandardOrderCutoffReq,
} from '@/services/outbound/outboundWaitingStatus/types'
import { ORDER_STATUS } from '@/services/outbound/outboundWaitingStatus/consts'
import dayjs from 'dayjs'
import { DATE_FORMAT } from '@/utils/consts'
import { Utils } from '@/utils/Utils'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { ExcelDownloadType } from '@/services/excel/excel.types'
import { pick } from 'lodash-es'

const PATHS = ['OUTBOUND', 'WAITING_STATUS', 'gi23']
const GET_ORDERS = [...PATHS, 'GET_ORDERS']
const GET_EXPECTED_ALLOCATION = [...PATHS, 'GET_EXPECTED_ALLOCATION']
const GET_B2B_ORDERS = [...PATHS, 'GET_B2B_ORDERS']
const GET_B2B_OUTBOUND_DOCUMENT = [...PATHS, 'GET_B2B_OUTBOUND_DOCUMENT']

// 출고조회 엑셀 다운로드 API
const EXCEL_API_ORDERS = '/api/v1/get-orders/download'

export const useGetOrders = (params: ApiPaginationReq<GetOrdersReq>): UseQueryResult<ApiPageResponse<OrderItem>, Error> => {
  const searchParams = {
    ...params,
    orderStatus: [ORDER_STATUS.ORDER_COLLECTED, ORDER_STATUS.ORDER_ACCEPTED, ORDER_STATUS.HOLD],
    collectedDateFrom: dayjs().subtract(13, 'day').format(DATE_FORMAT.date),
    collectedDateTo: dayjs().format(DATE_FORMAT.date),

    // TODO: [SCOPE B] 아래 다건 입력 구조 데이터 타입을 배열 -> 스트링으로 변환 필요 (API 수정 후 아래 코드 삭제 필요)
    orderNumbers: typeof params.orderNumbers === 'string' ? [params.orderNumbers] : params.orderNumbers,
  }
  return useQuery({
    queryKey: [...GET_ORDERS, searchParams],
    queryFn: () => getOrders(searchParams, 'GI23'),
    select: ({ data }) => {
      return { ...data, items: Utils.reformatArrayToAddIds(data.items) }
    },
    enabled: false,
  })
}

export const useGetExpectedAllocation = (params: GetExpectedAllocationReq) => {
  return useQuery({
    queryKey: [...GET_EXPECTED_ALLOCATION, params],
    queryFn: () => getExpectedAllocaton(params),
    select: ({ data }) => {
      const item = data.items[0]

      if (item && Array.isArray(item.centerData)) {
        return {
          ...item,
          centerData: [...item.centerData].sort((a, b) => a.warehouseName.localeCompare(b.warehouseName, 'ko')),
        }
      }

      return item
    },
    enabled: false,
  })
}

export const useExportExcelOnOrders = (params: GetOrdersDownload) => {
  const excelDownload = useExcelDownload({ config: { method: 'post' } })

  // TODO: [SCOPE B] 아래 다건 입력 구조 데이터 타입을 배열 -> 스트링으로 변환 필요 (API 수정 후 아래 코드 삭제 필요)
  const searchParams = {
    ...params,
    orderNumbers: typeof params.orderNumbers === 'string' ? [params.orderNumbers] : params.orderNumbers,
    orderOptionNumbers: typeof params.orderOptionNumbers === 'string' ? [params.orderOptionNumbers] : params.orderOptionNumbers,
    barcodes: typeof params.barcodes === 'string' ? [params.barcodes] : params.barcodes,
    shipperNames: typeof params.shipperNames === 'string' ? [params.shipperNames] : params.shipperNames,
    logisticsServiceTypes: typeof params.logisticsServiceTypes === 'string' ? [params.logisticsServiceTypes] : params.logisticsServiceTypes,
  }
  return {
    isLoading: excelDownload.isLoading,
    onExportData: (additionalParams?: ExcelDownloadType) => {
      excelDownload.onDownloadExcel(EXCEL_API_ORDERS, {
        ...searchParams,
        ...additionalParams,
        orderStatus: [ORDER_STATUS.INV_UNALLOCATED],
      })
    },
  }
}

export const useGetB2BOrders = (params: ApiPaginationReq<B2BGetOrdersReq>) => {
  return useQuery({
    queryKey: [...GET_B2B_ORDERS, params],
    queryFn: () => getB2BOrders(params),
    enabled: false,
    select: ({ data }) => {
      return { ...data, items: Utils.reformatArrayToAddIds(data.items) }
    },
  })
}

export const useGetB2BOutboundDocumentDetail = (params: ApiPaginationReq<GetB2BOutboundDocumentDetailReq>) => {
  return useQuery({
    queryKey: [...GET_B2B_OUTBOUND_DOCUMENT],
    queryFn: () => getB2BOutboundDocumentDetail(params),
    enabled: false,
  })
}

export const useRunStandardOrderCutoff = () => {
  return useMutation({
    mutationFn: (params: RunStandardOrderCutoffReq) => runStandardOrderCutoff(params),
  })
}
